import $ from 'jquery';
import './fetch.js';
import './XMLHttpRequest.js';
import Backbone from 'backbone';
import {fetchJson} from '@sinclair-digital/sbg-util/dist/es/fetch';
import {allPass, ascend, concat, descend, filter, map, pick, prop, sortWith, uniqBy, tap} from 'ramda';
import {identify as FSidentify} from '@fullstory/browser';

import AppContext from '@sinclair-digital/sbg-storyline-sdk/lib/storyline/AppContext';
import {getModuleRoutes, loadModulesForUser, listenForModuleUpdates, tryLoadPlugins} from './modules';
import Router from './router';
import {logError, logErrors} from '@sinclair-digital/stl-common/src/analytics/common';
import ActiveUserModel from '@sinclair-digital/stl-common/src/models/user/activeUserModel';
import {enhanceBackbone, enhanceBackboneWithUserModel, enhanceBackboneWithRouter} from '@sinclair-digital/stl-common/src/backboneExtras';
import {registerHandlebarHelpers} from '@sinclair-digital/stl-common/src/handlebarHelpers';
import {messageHandler} from '@sinclair-digital/stl-common/src/sinclair/sinclair.uiTools';
import {registerModules} from './registerModules';
import {initializeSessionTimeoutNotifier} from 'components/SessionTimeoutNotifier/initialize';
import {showErrorWithCode} from './components/LoginError';
import '@sinclair-digital/sbg-storyline-components/src/base/base.css';
import 'jquery-ui/themes/base/jquery-ui.css';
import 'datatables.net-dt/css/jquery.dataTables.css';
import 'bxslider/dist/jquery.bxslider.min.css';
import 'fancybox/dist/css/jquery.fancybox.css';
import './styles/sass/font-awesome.scss';
import './styles/sass/admin.scss';
// These are required for bootstrapping custom elements
import '@sinclair-digital/stl-common/src/widgets/custom/toggle';
import '@sinclair-digital/stl-common/src/widgets/custom/spinner';
import '@sinclair-digital/stl-common/src/widgets/custom/status';
import '@sinclair-digital/stl-common/src/widgets/custom/tooltipquestionmark';
import '@sinclair-digital/stl-common/src/widgets/sinclair.widgets';
import {storylineSDK} from '@sinclair-digital/core-tools/dist/storylineSDK';

storylineSDK.messageHandler = messageHandler;
window.storylineSDK = storylineSDK;

// eslint-disable-next-line @typescript-eslint/no-var-requires
const preLoadTemplate = require('templates/preLoadTemplate.hbs') as () => string;

enhanceBackbone();

const clearRoutes = (_Backbone, router) => {
    _Backbone.history.handlers = [];
    router._bindRoutes();
};

const updateRouter = (router, userModel, appContext) => modules => {
    clearRoutes(Backbone, router);
    return modules.setModuleRoutes(Backbone, userModel, appContext, router);
};

// Configure the application router after the userModel and plugin modules have loaded
// configureRouter :: @Backbone.Router
const configureRouter = (userModel, appContext) => {
    // Register plugin module routes the user has access to
    const routeOverrides = getModuleRoutes(Backbone, userModel);
    const router = new Router({routeOverrides, userModel, appContext});

    enhanceBackboneWithRouter(router);

    listenForModuleUpdates(updateRouter(router, userModel, appContext), 'storylineAppRouterUpdate');

    return router;
};

const stlSettingUrlsByEnvironment = {
    'Int': 'https://stl-settings.int-sinclairstoryline.com/int/modules',
    'Qa': 'https://stl-settings.qa-sinclairstoryline.com/qa/modules',
    'Prod': 'https://stl-settings.sinclairstoryline.com/prod/modules',
};

/**
 * Returns the full URL to fetch modules from, handling the existing domains, the local version and the new generic
 * non-prod domain.
 *
 * The known mappings that need to be handled are:
 * int.nmp-frontend.nonprod-sinclairstoryline.com =>
 *      https://stl-settings.int.nmp-frontend.nonprod-sinclairstoryline.com/int/modules
 * local.int-sinclairstoryline.com => https://stl-settings.int-sinclairstoryline.com/int/modules
 * int-sinclairstoryline.com => https://stl-settings.int-sinclairstoryline.com/int/modules
 *
 * @param {string} __hostname The hostname to build the new URL from.
 * @returns The full URL to the active module's endpoint.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention,@typescript-eslint/no-unused-vars
const activeModulesUriFromHost = (__hostname: string): string => {
    return stlSettingUrlsByEnvironment[globalThis.frontendStoryLineConfig.environment];
};

interface SPAModuleConfig {
    key: string;
    date: string;
    user: string;
    module: string;
    plugin: string;
    version: string;
}

interface SPAModulesConfig {
    modules: SPAModuleConfig[];
}

const getActiveConfiguration = () => fetchJson<SPAModulesConfig>(activeModulesUriFromHost(location.hostname));

const getLocalPluginOverrides = (): Pick<SPAModuleConfig, 'plugin' | 'version'>[] =>
    JSON.parse(localStorage.getItem('localPlugins') || '[]');

const loadActivePlugins = () =>
    getActiveConfiguration()
        // Get registry plugins, highest version first
        .then(prop('modules'))
        .then(map(pick(['plugin', 'version'])))

        // the following then allows us to use multiple versions in this code path
        .then(map(({plugin, version}) => {
            return {plugin, version: version.split('|')[0]};
        }))
        // filter any entries that do not have a version set
        .then(filter(({version}) => Boolean(version)))

        .then(sortWith([ascend(prop('plugin')), descend(prop('version'))]))

        // Prepend local plugins
        .then(concat(getLocalPluginOverrides()))

        // Remove any broken items
        .then(filter(allPass([prop('plugin'), prop('version')])))

        // Catch one item per plugin
        .then(uniqBy(prop('plugin')))

        // Load all plugins (but don't fail if unsuccessful)
        .then(tryLoadPlugins(true))
        .then(Promise.all.bind(Promise));

// Create & store token used to track V2 api requests (SPA-$username-$randomString)
const setApiTrackingToken = appContext =>
    localStorage.setItem(
        'X-Track-CorrelationToken',
        `SPA-${appContext.getUserName()}-${Math.random().toString(36)
            .substring(2)}`
    );

const setFullStoryUserIdentity = appContext => {
    const userName = appContext.getUserName();
    FSidentify(
        userName,
        {displayName: userName}
    );
};


export const initialize = function() {
    logErrors();
    const userModel = new ActiveUserModel();
    const appContext = new AppContext({userModel});

    // Expose debug model for overriding featureFlags via console
    window._debugModel = userModel.debugModel;

    enhanceBackboneWithUserModel(userModel);
    registerHandlebarHelpers(appContext);

    const userLoaded = new Promise((resolve, reject) => {
        const hasCache = (userModel.cache.get('chimUserProperties') ?? null) != null;
        const hasTargets = userModel.getAvailableTargetsJSON().length > 0;
        if (userModel.isCached() && hasCache && hasTargets) {
            userModel.fetch();
            resolve();
        } else {
            userModel
                .fetch()
                .then(resolve)
                .fail(reject);
        }
    })
        .catch(err => {
            logError({message: `user active call failed: ${err.message}`, isFatal: true});
            showErrorWithCode('user_active_failed', {
                responseStatus: err.status ? `HTTP ${err.status}` : '',
            });
            console.error(err);
            return Promise.reject(err);
        })
        .then(() => {
            setApiTrackingToken(appContext);
            setFullStoryUserIdentity(appContext);

            const targets = appContext.getAvailableTargets();
            if (targets.length === 0) {
                showErrorWithCode('no_available_targets', {
                    userId: appContext.getUserName() || 'N/A',
                });
                return Promise.reject(new Error('User has no available targets.'));
            }
        });

    $('#app-main').html(preLoadTemplate());

    return Promise.all([userLoaded, loadActivePlugins()])
        .then(() => loadModulesForUser(userModel))
        .then(registerModules)
        .then(() => configureRouter(userModel, appContext))
        .then(tap(initializeSessionTimeoutNotifier))
        .catch(err => {
            messageHandler('error', 'There was an error loading Storyline.', 'You may need to refresh your browser.');
            console.error(err);
        });
};
